import { EXTERNE_CONTACTEN_API, LEVERANCIERS_API } from "./contactApiPaths";
import { hasContactCardIdentity } from "@domaddress/shared";

/**
 * Gedeelde logica voor Excel-import en beheer-scripts (zelfde kolomnamen / koppeling als importpagina).
 */

export type ImportFieldDef = { key: string; label: string; required?: boolean; aliases: string[] };

/** Veld-sleutels (API) met Nederlandse label + synoniemen in kolom-koppeling. */
export const IMPORT_FIELD_DEFS: ImportFieldDef[] = [
  { key: "first_name", label: "Voornaam *", required: true, aliases: ["first name", "voornaam", "given", "first_name"] },
  { key: "last_name", label: "Achternaam *", required: true, aliases: ["last name", "achternaam", "surname", "family", "last_name"] },
  { key: "tussenvoegsel", label: "Tussenvoegsel", aliases: ["tussenvoegsel", "infix", "prefix", "insertion"] },
  { key: "initialen", label: "Initialen", aliases: ["initialen", "initials"] },
  {
    key: "email",
    label: "E-mail (privé)",
    aliases: ["e-mail", "email", "email prive", "e-mail prive", "e-mail (privé)", "private", "prive e-mail", "e-mailadres", "emailadres"],
  },
  {
    key: "email_werk",
    label: "E-mail (werk)",
    aliases: ["e-mail werk", "werkmail", "work email", "zakelijke e-mail", "work"],
  },
  {
    key: "dominicus_email",
    label: "E-mail Dominicus (eigen domein)",
    aliases: ["dominicus email", "dominicus-email", "dominicus e-mail", "werk dominicus", "organisatie e-mail"],
  },
  {
    key: "email_lijst",
    label: "E-mail lijst (alle_email / informatief / overlijden / geen)",
    aliases: ["email lijst", "e-mail lijst", "distributie", "nieuwsbriefkeuze", "lijst", "berichtenlijst"],
  },
  { key: "phone", label: "Telefoon vast", aliases: ["phone", "telefoon", "tel", "vast", "vast nummer", "landline", "kantoor"] },
  {
    key: "phone_mobile",
    label: "Telefoon mobiel",
    aliases: ["phone_mobile", "mobiel", "telefoon mobiel", "mobile", "gsm", "cel", "cell", "6-nummers"],
  },
  { key: "address", label: "Adres", aliases: ["address", "adres", "straat", "street"] },
  { key: "city", label: "Plaats", aliases: ["city", "plaats", "woonplaats", "town"] },
  { key: "postal_code", label: "Postcode", aliases: ["postal", "postcode", "post code", "zip", "postal code"] },
  { key: "country", label: "Land", aliases: ["country", "land", "nation"] },
  { key: "notes", label: "Oude notitie-veld (legacy)", aliases: ["notes", "legacy notes", "vrij veld oud"] },
  { key: "notitie", label: "Korte notitie", aliases: ["notitie", "korte notitie", "trefwoord"] },
  { key: "notities", label: "Lange notities", aliases: ["notities", "lange notities", "vrije opmerking"] },
  { key: "extra", label: "Extra", aliases: ["extra", "bijzonderheden"] },
  {
    key: "bedrijfsnaam",
    label: "Bedrijfsnaam (extern / leverancier)",
    aliases: ["bedrijfsnaam", "bedrijf", "company", "organisatie", "org", "firma"],
  },
  { key: "bellijn", label: "Bellijn (ja/nee)", aliases: ["bellijn", "op de bellijn", "bellijst"] },
  { key: "berichten", label: "Berichten (ja/nee)", aliases: ["berichten", "mailings", "e-mail algemeen"] },
  {
    key: "agv_ingestemd",
    label: "AVG / voorwaarden ingestemd (ja/nee)",
    aliases: ["agv", "avg", "agv ingestemd", "avg ingestemd", "algemene voorwaarden", "privacy", "terms", "akkoord"],
  },
];

/** Import voor externe contacten of leveranciers: geen verplichte voornaam/achternaam. */
export function isOrgContactImportPath(apiPath: string): boolean {
  return apiPath === EXTERNE_CONTACTEN_API || apiPath === LEVERANCIERS_API;
}

export function importFieldDefsForApiPath(apiPath: string): ImportFieldDef[] {
  if (!isOrgContactImportPath(apiPath)) return IMPORT_FIELD_DEFS;
  return IMPORT_FIELD_DEFS.map((f) => {
    if (f.key === "first_name") return { ...f, label: "Voornaam", required: false };
    if (f.key === "last_name") return { ...f, label: "Achternaam", required: false };
    if (f.key === "bedrijfsnaam") return { ...f, label: "Bedrijfsnaam (aanbevolen zonder persoonsnaam)" };
    return f;
  });
}

/** Of een geïmporteerde rij minstens één herkenbaar gegeven heeft. */
export function importRowHasIdentity(row: Record<string, string>): boolean {
  return hasContactCardIdentity({
    first_name: row.first_name,
    last_name: row.last_name,
    bedrijfsnaam: row.bedrijfsnaam,
    email: row.email,
    email_prive: row.email,
    email_werk: row.email_werk,
    dominicus_email: row.dominicus_email,
    phone: row.phone,
    phone_mobile: row.phone_mobile,
  });
}

export function importMappingIsComplete(mappings: Record<string, string>, apiPath: string): boolean {
  if (!isOrgContactImportPath(apiPath)) {
    return Boolean(mappings.first_name && mappings.last_name);
  }
  const keys = ["first_name", "last_name", "bedrijfsnaam", "email", "email_werk", "dominicus_email", "phone", "phone_mobile"];
  return keys.some((k) => Boolean(mappings[k]));
}

export function headerMatchesKey(header: string, def: ImportFieldDef): boolean {
  const h = String(header).trim().toLowerCase();
  if (h === def.key) return true;
  if (h === def.key.replace(/_/g, " ")) return true;
  return def.aliases.some((a) => a === h);
}

export function autoMapFromHeaders(row: string[], defs: ImportFieldDef[] = IMPORT_FIELD_DEFS) {
  const m: Record<string, string> = {};
  for (const def of defs) {
    const found = row.find((cell) => headerMatchesKey(cell, def));
    if (found != null) m[def.key] = found;
  }
  return m;
}

/** Bouwt één contact-object uit een datarij (zelfde logica als importpagina). */
export function mapRowToObject(
  headers: string[],
  mappings: Record<string, string>,
  row: unknown[]
): Record<string, string> {
  const contact: Record<string, string> = {};
  for (const [dbField, xlsHeader] of Object.entries(mappings)) {
    if (!xlsHeader) continue;
    const colIndex = headers.indexOf(xlsHeader);
    if (colIndex === -1) continue;
    const val = row[colIndex];
    const s = val != null && val !== "" ? String(val).trim() : "";
    if (s !== "") contact[dbField] = s;
  }
  return contact;
}
